import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useSettings, formatMoney, formatNumber } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { CommissionCard } from "./CommissionsPage";
import { OutNowPanel } from "../components/OutNowPanel";
import { useStatusLabel } from "../components/StatusBadge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";

import { tr } from "../lib/i18n";
const RANGES = [
  { key: "month", label: "d2.month" },
  { key: "quarter", label: "d2.quarter" },
  { key: "year", label: "d2.year" },
];

function ageClass(d) {
  if (d <= 30) return "age-0-30";
  if (d <= 60) return "age-31-60";
  if (d <= 90) return "age-61-90";
  return "age-90p";
}

export default function DashboardPage() {
  const { user, can } = useAuth();
  const canSeeMoney = ["admin", "management", "accounts"].includes(user?.role);
  const nav = useNavigate();
  const { settings } = useSettings();
  const { t, lang } = useT();
  const [range, setRange] = useState("month");
  const exportExcel = async () => {
    try {
      const { data } = await api.get(`/reports/sales/excel`, { params: { range }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `sales_${range}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [latestSales, setLatestSales] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: kpi }, { data: inv }, { data: sales }] = await Promise.all([
          api.get("/dashboard/kpis", { params: { range } }),
          api.get("/vehicles", { params: { status: "available", limit: 180, sort: "oldest" } }),
          api.get("/sales", { params: { limit: 6 } }).catch(() => ({ data: { items: [] } })),
        ]);
        setData(kpi); setVehicles(inv.items || []); setLatestSales(sales.items || []);
      } catch {}
    })();
  }, [range]);

  const inv = data?.inventory || {};
  const sales = data?.sales || { count: 0, revenue: 0, gross_profit: 0 };

  const dateStr = useMemo(() =>
    new Date().toLocaleDateString(lang === "ar" ? "ar-OM-u-nu-latn" : "en-GB", { day: "numeric", month: "long", year: "numeric" }), [lang]);
  const statusLabel = useStatusLabel();

  const revenueBars = (data?.charts?.sales_trend || []).map((r) => ({ month: r.month, revenue: r.revenue || 0, profit: r.profit || 0 }));
  const currentMonth = revenueBars[revenueBars.length - 1]?.month;

  const statusBuckets = (data?.charts?.status_donut || []).sort((a, b) => b.count - a.count);
  const totalStock = statusBuckets.reduce((s, x) => s + x.count, 0) || 1;

  const ageBuckets = useMemo(() => {
    const b = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
    vehicles.forEach((v) => {
      const d = v.days_in_stock ?? 0;
      if (d <= 30) b["0-30"]++; else if (d <= 60) b["31-60"]++;
      else if (d <= 90) b["61-90"]++; else b["90+"]++;
    });
    return b;
  }, [vehicles]);

  const avgDays = vehicles.length ? Math.round(vehicles.reduce((s, v) => s + (v.days_in_stock || 0), 0) / vehicles.length) : 0;
  const oldest = vehicles[0]?.days_in_stock || 0;
  const over90Cost = vehicles.filter((v) => (v.days_in_stock || 0) > 90).reduce((s, v) => s + (v.purchase_price || 0), 0);

  const statusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("avail")) return "var(--status-available-fg)";
    if (s.includes("reserv")) return "var(--status-reserved-fg)";
    if (s.includes("sold")) return "var(--status-sold-fg)";
    if (s.includes("prep") || s.includes("maint")) return "var(--text-muted)";
    return "var(--accent-500)";
  };

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="kicker">{t("dash.kicker", { date: dateStr })}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>
            {t("dash.welcome", { name: user?.full_name?.split(" ")[0] })}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="seg" data-testid="range-switcher">
            {RANGES.map((r) => (
              <button key={r.key} data-testid={`range-${r.key}`} className={range === r.key ? "active" : ""} onClick={() => setRange(r.key)}>
                {t(r.label)}
              </button>
            ))}
          </div>
          {can("reports", "view") && (
            <button className="btn-secondary" onClick={exportExcel} data-testid="export-btn"><Download size={14} strokeWidth={1.75} /> {t("d2.export")}</button>
          )}
          {can("vehicles", "create") && (
            <button className="btn-primary" onClick={() => nav("/inventory/new")} data-testid="add-vehicle">
              <Plus size={14} strokeWidth={1.75} /> {t("d2.add_vehicle")}
            </button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <CommissionCard />
      <div className="kpi-strip kpi-strip-dash" style={{ "--kpi-cols": canSeeMoney ? 5 : 3 }}>
        <KpiCell label={t("dash.k_stock")} value={formatNumber(inv.total || 0)} sub={t("d2.owned_consign", { a: formatNumber(inv.company_owned || 0), b: formatNumber(inv.consignment || 0) })} />
        <KpiCell label={t("dash.k_available")} value={formatNumber(inv.available || 0)} sub={t("d2.reserved_n", { n: formatNumber(inv.reserved || 0) })} />
        {canSeeMoney && <KpiCell label={t("dash.k_atAsking")} value={formatMoney(inv.total_asking_value || 0, settings)} sub={inv.total_cost_value != null ? t("dash.k_atCost", { v: formatMoney(inv.total_cost_value, settings) }) : null} />}
        <KpiCell label={t("dash.k_soldMo")} value={formatNumber(sales.count || 0)} sub={sales.revenue != null ? t("d2.revenue_v", { v: formatMoney(sales.revenue, settings) }) : null} />
        {canSeeMoney && <KpiCell label={t("dash.k_gpMtd")} value={formatMoney(sales.gross_profit || 0, settings)} />}
      </div>

      {/* Row 1: revenue bars + stock status */}
      {can("forms", "view") && <OutNowPanel />}
      <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), minmax(0, 1fr)))" }}>
        {canSeeMoney && <section className="panel" data-testid="chart-revenue" style={{ minWidth: 0 }}>
          <div className="panel-title"><div className="kicker">{t(canSeeMoney ? "dash.p_revenue" : "dash.p_revenueOnly")}</div></div>
          <div className="panel-body" style={{ height: 300, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueBars} margin={{ top: 20, right: 10, left: 0, bottom: 0 }} barGap={2} barCategoryGap="18%">
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={{ stroke: "var(--text)", strokeWidth: 2 }} tickLine={false} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} width={56} tickFormatter={(n) => new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n)} />
                <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} contentStyle={{ border: "1px solid var(--divider)", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="revenue" stackId="a">
                  {revenueBars.map((e, i) => <Cell key={i} fill={e.month === currentMonth ? "var(--accent)" : "var(--accent-300)"} />)}
                </Bar>
                {canSeeMoney && <Bar dataKey="profit" stackId="a" fill="var(--accent-800)" />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>}

        <section className="panel" data-testid="chart-stock-status" style={{ minWidth: 0 }}>
          <div className="panel-title"><div className="kicker">{t("dash.p_stockBy")}</div></div>
          <div className="panel-body">
            <div style={{ height: 48, display: "flex" }}>
              {statusBuckets.map((s, i) => (
                <div key={i} title={`${statusLabel(s.status)}: ${s.count}`} style={{ background: statusColor(s.status), width: `${(s.count / totalStock) * 100}%` }} />
              ))}
            </div>
            <table className="dense-table mt-4" style={{ width: "100%" }}>
              <tbody>
                {statusBuckets.map((s, i) => (
                  <tr key={i}>
                    <td style={{ width: 20 }}><span style={{ display: "inline-block", width: 10, height: 10, background: statusColor(s.status) }} /></td>
                    <td style={{ textTransform: "capitalize" }}>{statusLabel(s.status)}</td>
                    <td className="tabular font-medium" style={{ textAlign: "end" }}>{s.count}</td>
                    <td className="tabular opacity-60" style={{ textAlign: "end", whiteSpace: "nowrap" }}>{Math.round((s.count/totalStock)*100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Row 2: age wall + latest sales */}
      <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), minmax(0, 1fr)))" }}>
        <section className="panel" data-testid="chart-stock-age" style={{ minWidth: 0 }}>
          <div className="panel-title"><div className="kicker">{t("dash.p_stockAge")}</div></div>
          <div className="panel-body">
            <div className="flex flex-wrap gap-[3px] mb-4" style={{ maxWidth: "100%" }}>
              {vehicles.slice(0, 180).map((v) => (
                <button key={v.id} onClick={() => nav(`/inventory/${v.id}`)}
                  title={`${v.stock_number} · ${v.days_in_stock || 0}d`}
                  className={`age-dot ${ageClass(v.days_in_stock || 0)}`}
                  style={{ width: 16, height: 16 }} />
              ))}
            </div>
            <div className="grid gap-[2px]" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", background: "var(--divider)", border: "1px solid var(--divider)" }}>
              {Object.entries(ageBuckets).map(([k, v]) => (
                <button key={k} onClick={() => nav(`/inventory?age=${encodeURIComponent(k)}`)} style={{ background: "#fff", padding: "0.75rem 0.9rem", textAlign: "start", border: 0, cursor: "pointer" }}>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{k} {tr("x.days_2")}</div>
                  <div className="text-xl font-semibold mt-1 tabular">{v}</div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs opacity-70 flex flex-wrap gap-4">
              <span>{t("dash.avg_days", { d: avgDays })}</span>
              {canSeeMoney && <span>{t("dash.capital_tied")}: <strong className="tabular ms-1">{formatMoney(over90Cost, settings)}</strong></span>}
              <span>{t("dash.oldest", { d: oldest })}</span>
            </div>
          </div>
        </section>

        <section className="panel" data-testid="latest-sales" style={{ minWidth: 0 }}>
          <div className="panel-title"><div className="kicker">{t("dash.p_latest")}</div></div>
          <div className="panel-body">
            <ul className="space-y-0">
              {latestSales.slice(0, 6).map((s, i) => {
                const paid = (s.outstanding || 0) <= 0;
                return (
                  <li key={s.id || i} className="py-3 flex flex-wrap sm:flex-nowrap items-start sm:items-center justify-between gap-x-3 gap-y-1" data-testid={`latest-sale-${i}`} style={{ borderBottom: i === latestSales.slice(0,6).length - 1 ? "0" : "1px solid var(--divider-soft)" }}>
                    <div className="min-w-0 flex-1" style={{ flexBasis: "60%" }}>
                      <div className="text-sm font-medium sm:truncate" style={{ overflowWrap: "anywhere" }}>{s.vehicle_label || `${s.vehicle_make || ""} ${s.vehicle_model || ""}`.trim() || "—"}</div>
                      <div className="text-xs opacity-60 sm:truncate" style={{ overflowWrap: "anywhere" }}><bdi className="mono">{s.reference || s.invoice_number || s.reference_number}</bdi> · {s.buyer_name || "—"}</div>
                    </div>
                    <div className="text-end" style={{ whiteSpace: "nowrap", marginInlineStart: "auto" }}>
                      <div className="text-sm font-medium tabular">{formatMoney(s.selling_price || s.total || 0, settings)}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: paid ? "var(--status-available-fg)" : "var(--status-due)" }}>
                        {paid ? t("dash.paid") : t("dash.due", { v: formatMoney(s.outstanding || 0, settings) })}
                      </div>
                    </div>
                  </li>
                );
              })}
              {latestSales.length === 0 && <li className="text-sm opacity-60 py-6 text-center">{t("d2.no_sales")}</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

function KpiCell({ label, value, sub }) {
  return (
    <div data-testid="kpi-cell" style={{ minWidth: 0 }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ whiteSpace: "normal", overflowWrap: "normal", wordBreak: "keep-all", fontSize: "clamp(1.2rem, 1.5vw, 2rem)", lineHeight: 1.15 }}><bdi style={{ whiteSpace: "nowrap" }}>{value}</bdi></div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}
